import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AudioCallRouteParam, AudioConferenceCall, AudioConferenceCalls, OperatorConferenceCall, OperatorConferenceCalls } from "../../../models/audioCall";
import { CallStatusType } from "../../../configs";


export type TSliceCall = {
  audioCallStartTime?: string;
  audioCallEndTime?: string;
  audioCallStatus?: AudioCallRouteParam;
  audioCallCall?: string;
  audioConferenceCalls?: AudioConferenceCalls;
  audioOperatorCalls?: OperatorConferenceCalls;
};

function InitSliceCall(): TSliceCall {
  return {
    audioCallStartTime: "",
    audioCallEndTime: "",
    audioCallStatus: {
      prev: 0,
      current: 0,
    },
    audioConferenceCalls: [],
    audioOperatorCalls: [],
  };
}

const sliceAudioCall = createSlice({
  name: "sliceAudioCall",
  initialState: InitSliceCall(),
  reducers: {
    SetAudioCallStartTime: (
      state,
      action: PayloadAction<string>
    ): TSliceCall => ({
      ...state,
      audioCallStartTime: action.payload,
    }),
    SetAudioCallEndTime: (
      state,
      action: PayloadAction<string>
    ): TSliceCall => ({
      ...state,
      audioCallEndTime: action.payload,
    }),

    SetAudioCallStatus: (
      state,
      action: PayloadAction<AudioCallRouteParam>
    ): TSliceCall => ({
      ...state,
      audioCallStatus: action.payload,
    }),

    UpsertAudioConferenceCall: (
      state,
      action: PayloadAction<AudioConferenceCall>
    ): TSliceCall => {
      const existingCalls = state.audioConferenceCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.calledNumber === action.payload.calledNumber
      );

      if (index !== -1) {
        const updatedCalls = [...existingCalls];
        updatedCalls[index] = {
          ...updatedCalls[index],
          ...action.payload,
        };
        return {
          ...state,
          audioConferenceCalls: updatedCalls,
        };
      } else {
        return {
          ...state,
          audioConferenceCalls: [...existingCalls, action.payload],
        };
      }
    },

    UpdateAudioConferenceCallStatus: (
      state,
      action: PayloadAction<{
        calledNumber: string;
        current: CallStatusType;
        callSid: string; // <-- new
      }>
    ): TSliceCall => {
      const existingCalls = state.audioConferenceCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.calledNumber === action.payload.calledNumber
      );

      const updatedCalls = [...existingCalls];

      if (index !== -1) {
        const existingCall = updatedCalls[index];

        updatedCalls[index] = {
          ...existingCall,
          callSid: action.payload.callSid ?? existingCall.callSid, // update only if provided
          status: {
            prev: existingCall.status?.current ?? CallStatusType.Initiated,
            current: action.payload.current,
          },
        };
      } else {
        // Insert new call
        updatedCalls.push({
          calledNumber: action.payload.calledNumber,
          callSid: action.payload.callSid,
          status: {
            prev: CallStatusType.Initiated,
            current: action.payload.current,
          },
        });
      }

      return {
        ...state,
        audioConferenceCalls: updatedCalls,
      };
    },

    ClearAudioConferenceCalls: (state) => ({
      ...state,
      audioConferenceCalls: [],
    }),

    UpsertOperatorConferenceCall: (
      state,
      action: PayloadAction<OperatorConferenceCall>
    ): TSliceCall => {
      const existingCalls = state.audioOperatorCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.operatorId === action.payload.operatorId
      );

      if (index !== -1) {
        const updatedCalls = [...existingCalls];
        updatedCalls[index] = {
          ...updatedCalls[index],
          ...action.payload,
        };
        return {
          ...state,
          audioOperatorCalls: updatedCalls,
        };
      } else {
        return {
          ...state,
          audioOperatorCalls: [...existingCalls, action.payload],
        };
      }
    },

    UpdateOperatorConferenceCallStatus: (
      state,
      action: PayloadAction<{
        operatorId: string;
        current: CallStatusType;
      }>
    ): TSliceCall => {
      const existingCalls = state.audioOperatorCalls || [];

      const index = existingCalls.findIndex(
        (call) => call.operatorId === action.payload.operatorId
      );

      const updatedCalls = [...existingCalls];

      if (index !== -1) {
        const existingCall = updatedCalls[index];

        updatedCalls[index] = {
          ...existingCall,
          operatorStatus: {
            prev: existingCall.operatorStatus?.current ?? CallStatusType.Initiated,
            current: action.payload.current,
          },
        };
      } else {
        // Insert new call
        updatedCalls.push({
          operatorId: action.payload.operatorId,
          //the operator will be nameless
          // operatorName: action.payload.operatorName,
          operatorStatus: {
            prev: CallStatusType.Initiated,
            current: action.payload.current,
          },
        });
      }

      return {
        ...state,
        audioOperatorCalls: updatedCalls,
      };
    },


    ClearOperatorConferenceCalls: (state) => ({
      ...state,
      audioOperatorCalls: [],
    }),

    resetData: () => {
      return InitSliceCall();
    }
  },
});

export const ReducerAudioCall = sliceAudioCall.reducer;
export const ActionAudioCall = sliceAudioCall.actions;
